import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, LoaderCircle, Play, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { EASE } from "@/components/landing/Reveal";

const STATUS_UI = {
  completed: { label: "Terminée", pill: "bg-emerald-50 text-emeraude", dot: "border-emeraude bg-emeraude text-white" },
  in_progress: { label: "En cours", pill: "bg-bleu-light text-bleu", dot: "border-bleu bg-white text-bleu" },
  upcoming: { label: "À venir", pill: "bg-slate-100 text-slate-500", dot: "border-slate-200 bg-white text-slate-300" },
};

export default function MonParcours() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/parcours").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  const setStatus = async (step, status) => {
    setBusy(step.id);
    try {
      await api.patch(`/parcours/${step.id}`, { status });
      const { data: fresh } = await api.get("/parcours");
      setData(fresh);
      toast.success(status === "completed" ? `Étape « ${step.title} » terminée. Bravo !` : "Étape mise à jour.");
    } catch {
      toast.error("Impossible de mettre à jour l'étape.");
    } finally {
      setBusy(null);
    }
  };

  if (data === null)
    return <div className="flex justify-center py-24"><LoaderCircle className="h-7 w-7 animate-spin text-bleu" /></div>;
  if (data === false)
    return <p className="py-24 text-center text-sm text-slate-500">Impossible de charger votre parcours.</p>;

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="page-mon-parcours">
      <div>
        <h1 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">Mon parcours</h1>
        <p className="mt-1 text-sm text-slate-500">Votre feuille de route, étape par étape, jusqu'à l'obtention de votre objectif.</p>
      </div>

      <div className="card-integra p-5" data-testid="parcours-progress-card">
        <div className="flex items-center justify-between">
          <p className="text-sm font-bold text-slate-900">Progression globale</p>
          <p className="text-sm font-extrabold text-bleu" data-testid="parcours-progress-value">{data.progression}%</p>
        </div>
        <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-gradient-to-r from-bleu to-bleu-accent transition-all duration-1000" style={{ width: `${data.progression}%` }} />
        </div>
      </div>

      <ol className="relative space-y-4">
        <span className="absolute bottom-6 left-[19px] top-6 w-px bg-slate-200" aria-hidden />
        {data.steps.map((step, i) => {
          const ui = STATUS_UI[step.status];
          return (
            <motion.li
              key={step.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.06, ease: EASE }}
              className="relative flex gap-4"
              data-testid={`parcours-step-${step.key}`}
            >
              <span className={`relative z-10 mt-4 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold ${ui.dot}`}>
                {step.status === "completed" ? <Check className="h-4 w-4" /> : i + 1}
              </span>
              <div className={`card-integra flex-1 p-5 ${step.status === "in_progress" ? "border-bleu/25 ring-1 ring-bleu/10" : ""}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Étape {i + 1}</p>
                  <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${ui.pill}`}>{ui.label}</span>
                </div>
                <h3 className="mt-1.5 text-sm font-bold text-slate-900">{step.title}</h3>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">{step.desc}</p>
                <div className="mt-3.5 flex flex-wrap gap-2">
                  {step.status === "upcoming" && (
                    <button onClick={() => setStatus(step, "in_progress")} disabled={busy === step.id}
                      className="inline-flex items-center gap-1.5 rounded-full bg-bleu-light px-4 py-2 text-xs font-bold text-bleu transition-colors hover:bg-bleu hover:text-white disabled:opacity-50"
                      data-testid={`step-start-${step.key}`}>
                      <Play className="h-3 w-3" /> Commencer
                    </button>
                  )}
                  {step.status === "in_progress" && (
                    <button onClick={() => setStatus(step, "completed")} disabled={busy === step.id}
                      className="inline-flex items-center gap-1.5 rounded-full bg-emeraude px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
                      data-testid={`step-complete-${step.key}`}>
                      <Check className="h-3 w-3" /> Marquer comme terminée
                    </button>
                  )}
                  {step.status === "completed" && (
                    <button onClick={() => setStatus(step, "in_progress")} disabled={busy === step.id}
                      className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-500 transition-colors hover:border-bleu/40 hover:text-bleu disabled:opacity-50"
                      data-testid={`step-reopen-${step.key}`}>
                      <RotateCcw className="h-3 w-3" /> Rouvrir
                    </button>
                  )}
                </div>
              </div>
            </motion.li>
          );
        })}
      </ol>
    </div>
  );
}
